import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

import { NgForm } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  CachingUtilityService,
  DropDownConfiguration,
  DropDownData,
  EntityCacheProvider,
  LogoWidgetService,
  TestServerConfigService,
  UserAuth,
  ValuesExchangeProvider,
  ApplicationsService,
  IApplication,
  DevicesService,
  Device,
  TestMode,
  TestListService,
  RunTypeValue,
  UserService,
  LicensesPageService
} from '@nrwl/common-lib';
import { takeUntil } from 'rxjs/operators';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  outputParams,
  REMOVED
} from '@nrwl/test-setup';
import { filter, forEach, get } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { ScheduledIntervals } from '../../models/types';
import { MultiStepSetupService } from '../../multi-step-setup.service';
import { isNil } from 'lodash';
import { MultistepConfigMapping as ConfigFieldMapping } from '../../../json/multi-step-agent-model';
import { ScheduleLicense } from '@nrwl/licenses';

@Component({
  selector: 'nrwl-multistep-basic-tab',
  templateUrl: './multistep-basic-tab.component.html',
  styleUrls: ['./multistep-basic-tab.component.scss']
})
export class MultistepBasicTabComponent implements OnInit, OnDestroy {
  @ViewChild('cidrInput') el: ElementRef;
  @Input() tab: BaseSetupTab;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm', { static: true }) form: NgForm;
  //eslint-disable-next-line
  dialogRef: MatDialogRef<any>;
  showWarning = false;
  warningMessage = '';
  // eslint-disable-next-line
  selectedDevices: any;
  dropdownConfiguration: DropDownConfiguration;
  newTestMode = true;
  editMode = false;
  cloneMode = false;
  subContentList = [];
  appDropDown = [];
  testName: string;
  actualTestName: string;
  imagePath: string;
  scheduleInterval = ScheduledIntervals;
  repeatScheduleVal: number = this.scheduleInterval.FIVE_MINUTES;
  endScheduleVal: string;
  startScheduleVal = 'Now';
  selectedApp: DropDownData;
  allTestNames: Array<string>;
  nameExist: boolean;
  cacheObj: Subscription;
  hasReadWriteRole = false;
  browserSelection = true;
  scriptSelection = false;
  isLoading = false;
  selectedScriptName = '';
  scriptPicklist = [];
  siteDetails = [];
  runType = RunTypeValue;
  private readonly unsubscribe: Subject<void> = new Subject();
  selectedOriginator: Device;
  testMode = TestMode.DEFAULT;
  multistepScheduleLicense = ScheduleLicense.MULTISTEP;
  isDisabledScheduler = false;
  constructor(
    private readonly licenseService: LicensesPageService,
    public setupService: MultiStepSetupService,
    readonly entityCache: EntityCacheProvider,
    readonly serverConfigService: TestServerConfigService,
    readonly logoWidgetService: LogoWidgetService,
    readonly cachingUtilityService: CachingUtilityService,
    readonly exchange: ValuesExchangeProvider,
    readonly applicationsService: ApplicationsService,
    private testListService: TestListService,
    private userAuth: UserAuth,
    private readonly deviceService: DevicesService,
    private readonly userService: UserService
  ) {
    this.showWarning = false;
    this.nameExist = false;
  }

  ngOnInit(): void {
    this.initilizeData();
    this.newTestMode = this.setupService.mode === 'new';
    this.editMode = this.setupService.editMode;
    this.cloneMode = this.setupService.mode === 'clone';
    if (!(this.editMode || this.cloneMode)) {
      this.testName = `${this.testName}-${this.userService.getUserName()}-${new Date().getTime()}`;
    }
    this.cacheObj = this.testListService.cache.subscribe(data => {
      this.allTestNames = data.map(item => item.escapedName);
      if (this.testName) {
        this.checkIfNameAlreadyExist();
      }
    });
    this.licenseService.activeTestLicenseDetails.subscribe(licenseData => {
      if (licenseData?.length) {
        this.isDisabledScheduler = !licenseData.some(license =>
          this.multistepScheduleLicense?.includes(license.featureName)
        );
      }
      this.isDisabledScheduler
        ? this.onEndScheduleValChange('on-demand')
        : this.onEndScheduleValChange('on-going');
    });
  }

  initilizeData(): void {
    this.restoreAgentBasedConfig();
    this.restoreFormValues();
    this.tab.form = this.form;
    this.getDropDownConfiguration();
    this.hasReadWriteRole = this.userAuth.isAuthedFor('READ_WRITE');
    this.entityCache
      .findEntityCache('application')
      .getValuesAsync()
      .subscribe(appList => {
        const filteredList = filter(
          appList,
          app =>
            get(app.applicationData, 'applicationType') === 'User-Defined-Url' &&
            !app.applicationData.deleted
        );
        forEach(filteredList, element => {
          element.applicationData.hidden = false;
          this.subContentList.push(element.applicationData);
        });
        this.appDropDown = this.getAppDataInFormat(this.subContentList);
        this.populateApplication();
        const scriptName = this.restoreFieldValues(ConfigFieldMapping.SCRIPT_NAME);
        if (scriptName) {
          this.populateTestMethod(scriptName);
        }
      });
  }

  nameUpdated(): void {
    this.nameExist = false;
    if (this.testName) {
      const pattern = new RegExp('[?%/]');
      if (pattern.test(this.testName)) {
        this.form.controls['testName']?.setErrors({ regex: 'wrong' });
        this.form.controls['testName']?.markAsDirty();
      }
      this.checkIfNameAlreadyExist();
    }
  }
  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  restoreFormValues(): void {
    this.testName = this.restoreFieldValues(ConfigFieldMapping.ALIAS);
    this.actualTestName = this.testName;
    this.endScheduleVal = this.restoreFieldValues(ConfigFieldMapping.RUN_TYPE);
    this.selectedOriginator = this.restoreFieldValues(ConfigFieldMapping.SELECTED_ORIGINATOR);
    this.repeatScheduleVal = parseInt(this.restoreFieldValues(ConfigFieldMapping.INTERVAL));
  }

  onEndScheduleValChange(endVal: string): void {
    this.repeatScheduleVal = this.scheduleInterval.FIVE_MINUTES;
    this.endScheduleVal = endVal;
    this.onDataEmit(ConfigFieldMapping.RUN_TYPE, this.endScheduleVal, true);
  }

  onRepeatValChange(endVal: string): void {
    this.repeatScheduleVal = parseInt(endVal);
    this.onDataEmit(ConfigFieldMapping.INTERVAL, this.repeatScheduleVal, true);
  }

  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  selectTestUsingBrowser(): void {
    this.browserSelection = true;
    this.scriptSelection = false;
    this.onDataEmit(ConfigFieldMapping.SCRIPT_URL, null, true);
    this.onDataEmit(ConfigFieldMapping.PAGE_URL, this.selectedApp?.subContent, true);
  }

  changeScript(): void {
    const scriptUrl = `https://${this.serverConfigService.getHostname()}/entity/v1/application/${
      this.selectedApp.key
    }/script/${this.selectedScriptName}`;
    this.onDataEmit(ConfigFieldMapping.SCRIPT_URL, scriptUrl, true);
    this.onDataEmit(ConfigFieldMapping.SCRIPT_NAME, this.selectedScriptName, true);
  }

  selectTestUsingScript(): void {
    this.scriptSelection = true;
    this.browserSelection = false;
    this.onDataEmit(ConfigFieldMapping.PAGE_URL, null, true);
    const scriptUrl = `https://${this.serverConfigService.getHostname()}/entity/v1/application/${
      this.selectedApp.key
    }/script/${this.selectedScriptName}`;
    this.onDataEmit(ConfigFieldMapping.SCRIPT_URL, scriptUrl, true);
    this.onDataEmit(ConfigFieldMapping.SCRIPT_NAME, this.selectedScriptName, true);
  }

  getApplicationScripts(): void {
    const appId = parseInt(this.selectedApp?.key);
    this.isLoading = true;
    this.setupService
      .getApplicationScripts$(appId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          if (res?.length) {
            this.scriptPicklist = res
              .filter(script => script.isPreCheckPassed)
              .map(data => data.scriptName);
            this.selectedScriptName = this.scriptPicklist[0];
            this.isLoading = false;
            this.selectTestUsingBrowser();
          }
        },
        () => {
          this.isLoading = false;
          this.scriptPicklist = [];
          this.selectTestUsingBrowser();
        }
      );
  }

  // eslint-disable-next-line
  populateApplication(): void {
    const appId = this.restoreFieldValues(ConfigFieldMapping.SELECTED_APP_ID);
    if (appId) {
      this.selectedApp = this.appDropDown.find(x => x.key == appId);
    }
    this.onDataEmit(ConfigFieldMapping.SLECTED_APP, this.selectedApp, true);
  }

  populateTestMethod(scriptName: string): void {
    if (scriptName) {
      this.selectedScriptName = scriptName;
      this.selectTestUsingScript();
      this.scriptPicklist.push(scriptName);
    } else {
      this.selectTestUsingBrowser();
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onAgentChange(data: any): void {
    const engineAInfo = [],
      selectedOriginator = [];
    let dataValue;
    if (!data.length) {
      dataValue = [data];
    } else {
      dataValue = data;
    }
    for (let i = 0; i < dataValue.length; i++) {
      const originatorInfo = this.deviceService.findDeviceByID(dataValue[i].originatorId);
      selectedOriginator.push(originatorInfo);
    }
    this.onDataEmit('selectedOriginator', selectedOriginator, true);
  }

  getDropDownConfiguration(): void {
    this.dropdownConfiguration = {
      isDisableRipple: true,
      outline: 'outline',
      isDisableOptionCentering: true,
      className: 'app-dropdown',
      panelClass: 'app-panel',
      placeholderValue: 'Select application',
      isSearchable: true,
      selectTriggerMatIcon: 'display_settings',
      isDividerEnableInOptions: true,
      isSelectAll: true,
      isWarningAvailable: true,
      isDisabled: this.editMode ? true : false,
      isLogoImage: true,
      searchConfiguration: {
        isNameAllowed: true,
        isSubContentAllowed: true,
        isSubContent1Allowed: true,
        isSubDetailsAllowed: true
      },
      enableRowIcons: false
    };
  }

  // eslint-disable-next-line
  getAppDataInFormat(listData: Array<IApplication>): DropDownData[] {
    return this.getDropDownData(listData);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDropDownData(appList: Array<IApplication>): DropDownData[] {
    const dropDownData: DropDownData[] = [];
    let tempData: DropDownData = {
      value: '',
      key: '',
      subContent: '',
      matOptionprefixSrcImage: '',
      curlURL: ''
    };
    appList?.map(element => {
      tempData = Object.assign({}, tempData);
      tempData.matOptionprefixSrcImage = this.updateIcon(element.url, 'images/cloud.svg');
      tempData.value = element.name;
      tempData.subContent = element.url;
      tempData.key = element.id.toString();
      tempData.curlURL = element.curlUrl;
      dropDownData.push(tempData);
    });
    return dropDownData;
  }

  updateIcon(serviceUrl: string, defaultUrl: string): string {
    const serviceUrlCopy = serviceUrl;
    if (serviceUrl && serviceUrl.indexOf('tcp:') < 0) {
      this.logoWidgetService.getImage(
        serviceUrl,
        defaultUrl,
        (originalUrl: string, imgData: string) => {
          if (serviceUrlCopy !== originalUrl) {
            return;
          }
          this.imagePath = imgData;
        }
      );
    } else {
      this.imagePath = defaultUrl;
    }
    return this.imagePath;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  applicationSelection(event: DropDownData): void {
    this.selectedApp = this.appDropDown.find(x => x.key === event.key);
    this.onDataEmit(ConfigFieldMapping.SLECTED_APP, this.selectedApp, true);
    this.getApplicationScripts();
  }
  checkIfNameAlreadyExist(): void {
    if (this.editMode) {
      this.allTestNames = this.allTestNames.filter(name => name !== this.actualTestName);
    }
    if (this.allTestNames.some(item => item.toLowerCase() === this.testName.toLowerCase())) {
      this.onDataEmit(ConfigFieldMapping.ALIAS, null, false);
      this.onDataEmit(ConfigFieldMapping.TEST_NAME, null, false);
      this.nameExist = true;
    } else {
      this.nameExist = false;
      this.onDataEmit(ConfigFieldMapping.ALIAS, this.testName, true);
      this.onDataEmit(ConfigFieldMapping.TEST_NAME, this.testName, true);
    }
  }

  ngOnDestroy() {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
